import { logger as baseLogger } from "matrix-js-sdk/src/logger";

import { DthTokenCrypto, type IdbEncryptedRecord, DthTokenStore } from "./DthTokenCrypto";
import { hmacSha256Hex } from "./utils/utils";
import { DELETE_RECOVERY_KEY_ON_ACCOUNT_CHANGE } from "../constants";

const logger = baseLogger.getChild("DthRecoveryKeyManager");

/**
 * Manager for the user's secret-storage / key-backup recovery key.
 *
 * The recovery key is persisted in IndexedDB through {@link DthTokenCrypto},
 * sharing its TOKENS_STORE under the id `"${userId}:RECOVERY_KEY"`. Two
 * encryption layers protect it at rest:
 *
 *  1. **Inner layer (password-bound):** the recovery key string is encrypted
 *     with AES-GCM using a key derived from `hmacSha256Hex(password, userId)`
 *     — exactly the same hashing scheme used elsewhere for user passwords.
 *     The plaintext recovery key cannot be recovered without the user's
 *     account password.
 *  2. **Outer layer (storage-bound):** the inner payload is then wrapped by
 *     {@link DthTokenCrypto.encrypt} which adds AES-GCM with the non-extractable
 *     IDB master key plus an HMAC integrity tag, providing the same at-rest
 *     guarantees as the validation tokens.
 *
 * Cross-user cleanup follows the same pattern as `DthValidationTokenManager`:
 * saving with `resetOtherUsers = true` removes every record belonging to a
 * different user, so a new login wipes the previous user's recovery data.
 */
const RECORD_TYPE = "RECOVERY_KEY";

// Recovery keys do not have a meaningful TTL. Use a far-future Unix-second
// value so the IDB-level expiry index never picks them up.
const FAR_FUTURE_SECONDS = 32503680000; // year 3000

interface InnerPayload {
    /** base64-encoded 12-byte AES-GCM IV. */
    iv: string;
    /** base64-encoded AES-GCM ciphertext (recovery-key string). */
    ciphertext: string;
    userId: string;
    timestamp: number;
}

function recordId(userId: string): string {
    return `${userId}:${RECORD_TYPE}`;
}

function hexToBytes(hex: string): Uint8Array {
    const out = new Uint8Array(hex.length >> 1);
    for (let i = 0; i < out.length; i++) {
        out[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return out;
}

function bytesToB64(bytes: Uint8Array): string {
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
}

function b64ToBytes(b64: string): Uint8Array {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
}

/**
 * Derive a non-extractable AES-GCM key from the user's account password.
 * The key bytes are the raw 32-byte output of `hmacSha256Hex(password, userId)`.
 */
async function deriveAesKey(password: string, userId: string): Promise<CryptoKey> {
    const hashHex = await hmacSha256Hex(password, userId);
    const keyBytes = hexToBytes(hashHex);
    return crypto.subtle.importKey(
        "raw",
        // WebCrypto typing variations in different TS DOM libs — pass the
        // underlying ArrayBuffer to avoid `SharedArrayBuffer`-related union
        // incompatibilities in this workspace's lib definitions.
        keyBytes.buffer as ArrayBuffer,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"],
    );
}

export class DthRecoveryKeyManager {
    private static readonly RECOVERY_KEY_STORE = DthTokenStore.RecoveryKeys;

    /**
     * Persist the recovery key for a user, encrypted under their account password.
     *
     * @param userId          Account email / id (same value used elsewhere).
     * @param password        Password to derive the AES key.
     * @param recoveryKey     The encoded recovery key string (base58).
     * @param resetOtherUsers When true, removes any record belonging to a different user.
     */
    public static async save(
        userId: string,
        password: string,
        recoveryKey: string,
        resetOtherUsers = DELETE_RECOVERY_KEY_ON_ACCOUNT_CHANGE,
    ): Promise<void> {
        if (!userId || !password || !recoveryKey) return;

        try {
            const aesKey = await deriveAesKey(password, userId);
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const ctBuf = await crypto.subtle.encrypt(
                { name: "AES-GCM", iv: iv as any },
                aesKey,
                new TextEncoder().encode(recoveryKey),
            );

            const payload: InnerPayload = {
                iv: bytesToB64(iv),
                ciphertext: bytesToB64(new Uint8Array(ctBuf)),
                userId,
                timestamp: Date.now(),
            };

            const id = recordId(userId);
            const record = await DthTokenCrypto.encrypt(JSON.stringify(payload), id, FAR_FUTURE_SECONDS);
            await DthTokenCrypto.saveRecord(record, DthRecoveryKeyManager.RECOVERY_KEY_STORE);
        } catch (e) {
            logger.warn("save: failed to encrypt/persist recovery key", e);
            return;
        }

        if (resetOtherUsers) await this.removeOtherUsers(userId);
    }

    /**
     * Decrypt and return the recovery-key string for a user, or null if it is
     * absent, the password is wrong, or any integrity check fails.
     */
    public static async get(userId: string, password: string): Promise<string | null> {
        if (!userId || !password) return null;
        const id = recordId(userId);

        let record: IdbEncryptedRecord | null;
        try {
            record = await DthTokenCrypto.loadRecord(id, DthRecoveryKeyManager.RECOVERY_KEY_STORE);
        } catch (e) {
            logger.warn("get: IDB load failed", e);
            return null;
        }
        if (!record) return null;

        const outer = await DthTokenCrypto.decrypt(record).catch(() => null);
        if (!outer) {
            logger.warn("get: outer decryption/integrity failure — discarding record", id);
            void DthTokenCrypto.deleteRecord(id, DthRecoveryKeyManager.RECOVERY_KEY_STORE).catch(() => {});
            return null;
        }

        let payload: InnerPayload;
        try {
            payload = JSON.parse(outer) as InnerPayload;
        } catch {
            logger.warn("get: JSON parse failure — discarding record", id);
            void DthTokenCrypto.deleteRecord(id, DthRecoveryKeyManager.RECOVERY_KEY_STORE).catch(() => {});
            return null;
        }

        try {
            const aesKey = await deriveAesKey(password, userId);
            const ptBuf = await crypto.subtle.decrypt(
                { name: "AES-GCM", iv: b64ToBytes(payload.iv) as any },
                aesKey,
                b64ToBytes(payload.ciphertext) as any,
            );
            return new TextDecoder().decode(ptBuf);
        } catch (e) {
            // Wrong password or tampered inner payload — keep the record so a
            // subsequent call with the correct password can still succeed.
            logger.warn("get: password-based decryption failed", e);
            return null;
        }
    }

    /**
     * Returns true if a recovery-key record exists for the given user.
     * Does not require the password.
     */
    public static async has(userId: string): Promise<boolean> {
        if (!userId) return false;
        try {
            const record = await DthTokenCrypto.loadRecord(recordId(userId), DthRecoveryKeyManager.RECOVERY_KEY_STORE);
            return !!record;
        } catch {
            return false;
        }
    }

    /**
     * Remove the recovery key record for a specific user.
     */
    public static async remove(userId: string): Promise<void> {
        if (!userId) return;
        await DthTokenCrypto.deleteRecord(recordId(userId), DthRecoveryKeyManager.RECOVERY_KEY_STORE).catch((e) =>
            logger.warn("remove: IDB delete failed", e),
        );
    }

    /**
     * Handle a change of the currently authenticated user.
     * Wipes all stored recovery keys when `currentUserId` is undefined,
     * otherwise removes those belonging to other users.
     */
    public static async onUserChanged(currentUserId?: string): Promise<void> {
        if (!currentUserId) {
            await DthTokenCrypto.clearAll(DthRecoveryKeyManager.RECOVERY_KEY_STORE).catch(() => {});
            return;
        }
        await this.removeOtherUsers(currentUserId);
    }

    private static async removeOtherUsers(currentUserId: string): Promise<void> {
        const prefix = `${currentUserId}:`;
        const ids = await DthTokenCrypto.getAllIds(DthRecoveryKeyManager.RECOVERY_KEY_STORE).catch(() => [] as string[]);
        const others = ids.filter((id) => !id.startsWith(prefix));
        if (others.length === 0) return;
        await Promise.all(others.map((id) => DthTokenCrypto.deleteRecord(id, DthRecoveryKeyManager.RECOVERY_KEY_STORE)));
    }
}
